/**
 * SetPasswordModal — a super-admin sets a user's password.
 *
 * Backend: POST /console/users/{id}/password with `{ password }`. By id rather
 * than email, because provisioned accounts often have no email and so cannot
 * use self-service reset.
 *
 * Auth: caller shows this to super-admins only; the backend requires super-admin.
 */

import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Modal } from '@shared-ui/components';
import { userApi } from '../../api/userApi';

interface SetPasswordModalProps {
  isOpen: boolean;
  userId: string;
  userName: string;
  onClose: () => void;
}

const MIN_LENGTH = 6;

export function SetPasswordModal({ isOpen, userId, userName, onClose }: SetPasswordModalProps) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  // Reset the form each time the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setPassword('');
    setConfirm('');
    setError('');
    setDone(false);
  }, [isOpen]);

  const setMut = useMutation({
    mutationFn: () => userApi.setUserPassword(userId, password),
    onSuccess: () => setDone(true),
    onError: (err: Error) => setError(err.message),
  });

  const tooShort = password.length > 0 && password.length < MIN_LENGTH;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSave = password.length >= MIN_LENGTH && confirm === password;

  const inputStyle = { width: '100%', padding: 'var(--spacing-sm)', borderRadius: 4, border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', boxSizing: 'border-box' as const };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Set password — ${userName}`}
      size="sm"
      footer={
        done ? (
          <Button onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              onClick={() => { setError(''); setMut.mutate(); }}
              disabled={!canSave || setMut.isPending}
              loading={setMut.isPending}
            >
              Set password
            </Button>
          </>
        )
      }
    >
      {done ? (
        <div style={{ padding: 'var(--spacing-sm)', background: 'var(--status-success-bg, #e6f5ea)', color: 'var(--status-success-text, #1a7f37)', borderRadius: 4 }}>
          Password updated. {userName} can sign in with it now.
        </div>
      ) : (
        <>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 'var(--spacing-sm)' }}>
            Replaces the current password. The user is not notified — tell them the new one.
          </div>

          <label style={{ display: 'block', marginBottom: 'var(--spacing-sm)' }}>
            <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>New password</div>
            <input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              disabled={setMut.isPending}
              style={inputStyle}
            />
          </label>

          <label style={{ display: 'block' }}>
            <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Confirm password</div>
            <input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              disabled={setMut.isPending}
              style={inputStyle}
            />
          </label>

          {(tooShort || mismatch) && (
            <div style={{ marginTop: 'var(--spacing-xs, 4px)', fontSize: 12, color: 'var(--status-error-text)' }}>
              {tooShort ? `At least ${MIN_LENGTH} characters.` : 'Passwords do not match.'}
            </div>
          )}

          {error && (
            <div style={{ marginTop: 'var(--spacing-sm)', padding: 'var(--spacing-sm)', background: 'var(--status-error-bg)', color: 'var(--status-error-text)', borderRadius: 4 }}>
              {error}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
